// src/components/SurfaceHeader.jsx
import React from 'react';

/**
 * SurfaceHeader component with title, subtitle, Java-version slider,
 * decorative wave, underwater effect, and theme-specific backgrounds.
 *
 * Props:
 *   - theme: 'light' | 'dark'
 *   - lightBgUrl?: string
 *   - darkBgUrl?: string
 *   - sliderItems: (string|null)[]   // [null, '8','9',...]
 *   - sliderIndex: number           // current index in sliderItems
 *   - onSliderChange?: (index: number) => void
 *   - allZones: array of all zone objects (passed from App.jsx)
 */
export default function SurfaceHeader({
  theme = 'light',
  lightBgUrl,
  darkBgUrl,
  sliderItems = [],
  sliderIndex = 0,
  onSliderChange,
  allZones, // ← expect this prop rather than importing getZonesData
}) {
  const isLight = theme === 'light';
  const textColor = isLight ? 'text-gray-900' : 'text-gray-100';
  const lineColor = isLight ? 'border-gray-900' : 'border-gray-100';
  const waveFill = isLight ? '#836953' : '#1f2937';

  // Fallback gradient if no background image
  const fallbackGradient = isLight
    ? 'bg-gradient-to-b from-green-200 to-blue-200'
    : 'bg-gradient-to-b from-gray-900 to-black';

  // CSS gradient overlay on top of the background image
  const gradientCss = isLight
    ? 'linear-gradient(to bottom, rgba(134,239,172,0.6), rgba(147,197,253,0.6))'
    : 'linear-gradient(to bottom, rgba(31,41,55,0.7), rgba(30,58,138,0.7))';

  // Choose the correct background image URL based on theme
  const bgUrl = isLight ? lightBgUrl : darkBgUrl;
  const style = bgUrl
    ? {
      backgroundImage: `${gradientCss}, url(${bgUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
    : {};

  // Slider bounds
  const min = 0;
  const max = sliderItems.length - 1;

  return (
    <section
      id="surface"
      className={`relative snap-start flex flex-col justify-center items-center min-h-screen px-4 overflow-hidden ${!bgUrl ? fallbackGradient : ''
        }`}
      style={style}
    >
      {bgUrl && isLight && (
        <div className="absolute inset-0 bg-black bg-opacity-20 pointer-events-none" />
      )}
      <a
        href="https://www.spaghetticodejungle.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="relative z-10 mb-4"
      >
        <img
          src="/images/spj_logo.png"
          alt="Spaghetti Code Jungle Logo"
          className="h-32 w-auto mx-auto hover:scale-105 transition-transform duration-200"
        />
      </a>


      <h1 className={`relative text-5xl font-extrabold mb-2 ${textColor}`}>
        The Amazon River of Java
      </h1>
      <hr className={`relative w-24 border-t-2 ${lineColor} mb-6`} />
      <p className={`relative text-xl max-w-2xl text-center mb-8 ${textColor}`}>
        Dive through Java’s evolution from the surface shallows to the depths of its
        feature-rich core.
      </p>

      {/* Version Slider */}
      <div className="relative z-10 w-full max-w-lg mb-12">
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={sliderIndex}
          onChange={(e) => onSliderChange?.(Number(e.target.value))}
          className="w-full h-6 focus:outline-none"
        />
        <div className="mt-2 flex justify-between text-xs text-black dark:text-white">
          {sliderItems.map((ver, idx) => {
            // Look up the first zone object that introduced this version, using the allZones prop.
            const zoneForVer =
              ver === null
                ? null
                : allZones.find((z) =>
                  z.versions.some((v) => v.versionNumber === ver)
                );

            const href = zoneForVer ? `#${zoneForVer.id}` : '#';
            return (
              <a
                key={idx}
                href={href}
                onClick={(e) => {
                  e.preventDefault();
                  onSliderChange?.(idx);
                  if (zoneForVer) {
                    document
                      .getElementById(zoneForVer.id)
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className={`text-center ${idx === sliderIndex ? 'font-semibold text-blue-600' : ''
                  }`}
              >
                {ver === null ? 'Java ' : ` ${ver} `}
              </a>
            );
          })}
        </div>
      </div>

      {/* Animated wave at bottom */}
      <div className="absolute bottom-0 left-0 w-full h-24">
        <svg
          viewBox="0 0 1200 100"
          preserveAspectRatio="none"
          className="w-full h-full block"
        >
          <g>
            <defs>
              <path
                id="wavePath"
                d="M0,30 C150,80 450,0 600,30 C750,60 1050,30 1200,50 L1200,100 L0,100 Z"
              />
            </defs>
            <use href="#wavePath" fill={waveFill} x="0" y="0" />
            <use href="#wavePath" fill={waveFill} x="1200" y="0" />
            <animateTransform
              attributeName="transform"
              type="translate"
              from="0,0"
              to="-1200,0"
              dur="10s"
              repeatCount="indefinite"
            />
          </g>
        </svg>
      </div>
    </section>
  );
}
